import { useFormik } from 'formik'
import React, { useState } from 'react'
import { Switch } from '@headlessui/react'
import { useSaveEventTierMutation } from '../../../redux-toolkits/events/events.slice'
import ModalComponent from '..'
import { Button } from '../../button'
import { Checkbox } from '../../inputs/Checkbox'
import { TextInput } from '../../inputs/textInput'

import { AddTicketTierSchema } from '../../../validation/validate'

import { NumericFormat } from 'react-number-format'
import {
  formatCurrency,
  getCurrencySymbol,
  parseCurrencyToNumber,
} from '../../../helpers'
import { EventTicketTier } from '../../../redux-toolkits/events/events.type'

export const AddTicketTierModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (ticket: EventTicketTier) => void
  ticket?: EventTicketTier
  eventId?: string
  defaultMarkupValue?: number
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  ticket,
  eventId,
  defaultMarkupValue = 0,
}) => {
  const [saveEventTier, { isLoading: savingTier }] = useSaveEventTierMutation()
  const [saveError, setSaveError] = useState('')
  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: ticket?.name ?? '',
      description: ticket?.description ?? '',
      isActive: ticket?.isActive ?? true,
      markupValue: String(ticket?.markupValue ?? defaultMarkupValue),
      price: ticket?.price ?? '',
      is_group_tier: ticket?.is_group_tier ?? false,
      admitted_count: String(
        ticket?.is_group_tier ? ticket.admitted_count ?? 2 : 2,
      ),
      unit_price: ticket?.unit_price ?? '',
      quantity: ticket?.quantity ?? '',
    },
    enableReinitialize: true,
    validationSchema: AddTicketTierSchema,
    onSubmit: async (values) => {
      setSaveError('')
      const unitPrice = parseCurrencyToNumber(values.unit_price)
      const admittedCount = values.is_group_tier
        ? Number(values.admitted_count)
        : 1
      const price = values.is_group_tier
        ? (Math.round(unitPrice * 100) * admittedCount) / 100
        : parseCurrencyToNumber(values.price)
      const tier: EventTicketTier = {
        ...ticket,
        ...values,
        markupValue: Number(values.markupValue),
        price,
        quantity: parseCurrencyToNumber(values.quantity),
        admitted_count: admittedCount,
        unit_price: values.is_group_tier ? unitPrice : price,
        total_price: price,
      }
      if (eventId) {
        const response = await saveEventTier({ eventId, tier })
        if (!response.data?.success) {
          setSaveError(
            response.data?.message ?? 'Unable to save tier. Please try again.',
          )
          return
        }
        onSuccessCallback(response.data.data)
      } else {
        onSuccessCallback(tier)
      }
      resetForm()
    },
  })
  const unitPrice = parseCurrencyToNumber(values.unit_price)
  const admittedCount = Number(values.admitted_count)
  const basePrice = values.is_group_tier
    ? (Math.round(unitPrice * 100) * admittedCount) / 100
    : parseCurrencyToNumber(values.price)
  const customerPrice =
    basePrice + (basePrice * Number(values.markupValue)) / 100
  const validGroupPrice =
    admittedCount >= 2 && Number.isInteger(admittedCount) && unitPrice > 0
  const displayPrice = formatCurrency(customerPrice, {
    currency: 'NGN',
    decimalPlaces: 2,
  })

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        setSaveError('')
        onCloseCallback()
      }}
      title={ticket ? 'Edit Tier' : 'Add Tier'}
      childrenClassName="md:!max-w-[420px]"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <TextInput
          type="text"
          name="name"
          title="Name"
          placeholder="Name"
          value={values.name}
          onBlur={handleBlur}
          onChange={handleChange}
          errors={errors.name && touched.name ? errors.name : ''}
          hasIcon={false}
          readOnly={false}
          showAsReadOnly
          className=""
        />

        <TextInput
          type="textarea"
          name="description"
          title="Description (optional)"
          placeholder="What does this ticket include?"
          value={values.description}
          onBlur={handleBlur}
          onChange={handleChange}
          errors={
            errors.description && touched.description ? errors.description : ''
          }
          hasIcon={false}
        />

        <div className="flex flex-col gap-2">
          <Checkbox
            checkStatus={values.isActive}
            handleChange={(checked) => {
              void setFieldValue('isActive', checked)
            }}
            text="Show tier to customers"
          />
          <p className="text-sm text-sec-black">
            Hidden tiers cannot be purchased. Existing tickets remain valid.
          </p>
        </div>

        <div className="flex items-center justify-between gap-4">
          <span className="font-medium">Enable Multi-Ticket / Group Tier</span>
          <Switch
            aria-label="Enable Multi-Ticket / Group Tier"
            checked={values.is_group_tier}
            onChange={(enabled) => {
              void setFieldValue('is_group_tier', enabled)
            }}
            className="group inline-flex h-6 w-10 shrink-0 items-center rounded-full bg-gray-200 transition data-[checked]:bg-black"
          >
            <span className="size-5 translate-x-0.5 rounded-full bg-white transition group-data-[checked]:translate-x-[18px]" />
          </Switch>
        </div>

        {values.is_group_tier ? (
          <>
            <TextInput
              type="number"
              name="admitted_count"
              title="Number of People Admitted"
              aria-label="Number of People Admitted"
              aria-describedby="admitted-count-help"
              min={2}
              step={1}
              value={values.admitted_count}
              onChange={handleChange}
              onBlur={handleBlur}
              hasIcon={false}
              errors={touched.admitted_count ? errors.admitted_count : ''}
              extra={
                <span
                  tabIndex={0}
                  title="How many individuals does this single pass admit?"
                  aria-label="How many individuals does this single pass admit?"
                >
                  ⓘ
                </span>
              }
            />
            <p id="admitted-count-help" className="text-sm text-sec-black">
              How many individuals does this single pass admit?
            </p>
            <NumericFormat
              customInput={TextInput}
              name="unit_price"
              title="Single Price per Person"
              aria-label="Single Price per Person"
              placeholder="₦7,500"
              prefix={getCurrencySymbol('NGN')}
              thousandSeparator
              decimalScale={2}
              value={values.unit_price}
              onChange={handleChange}
              onBlur={handleBlur}
              hasIcon={false}
              errors={touched.unit_price ? errors.unit_price : ''}
            />
          </>
        ) : (
          <>
            <NumericFormat
              suffix={undefined}
              prefix={getCurrencySymbol('NGN')}
              decimalScale={2}
              thousandSeparator
              customInput={TextInput}
              type="text"
              name="price"
              placeholder="Price"
              title="Base price"
              value={values.price}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={errors.price && touched.price ? errors.price : ''}
              hasIcon={false}
            />
          </>
        )}

        <TextInput
          type="number"
          name="markupValue"
          title="Markup (%)"
          aria-label="Markup (%)"
          placeholder="0"
          min={0}
          max={100}
          step="any"
          value={values.markupValue}
          onBlur={handleBlur}
          onChange={handleChange}
          errors={
            errors.markupValue && touched.markupValue ? errors.markupValue : ''
          }
          hasIcon={false}
        />

        {values.is_group_tier ? (
          <>
            <p className="text-sm text-sec-black">
              Bundle subtotal before markup:{' '}
              {formatCurrency(basePrice, { currency: 'NGN', decimalPlaces: 2 })}
            </p>
            <TextInput
              name="total_price"
              title="Customer Total Price"
              aria-label="Customer Total Price"
              value={validGroupPrice ? displayPrice : ''}
              placeholder="Enter a price and headcount"
              readOnly
              showAsReadOnly
              hasIcon={false}
            />
          </>
        ) : (
          <p className="text-sm text-sec-black">
            Customer price: {displayPrice}
          </p>
        )}

        <div
          className="rounded-lg border border-divider-gray bg-bg-gray p-4"
          aria-live="polite"
        >
          <p className="text-sm text-sec-black mb-2">
            Good Online customer preview
          </p>
          <p className="font-medium">
            {values.name || 'Tier name'} —{' '}
            {values.is_group_tier && !validGroupPrice
              ? 'Enter group pricing'
              : displayPrice}
          </p>
          {values.is_group_tier && validGroupPrice && (
            <p className="text-sm mt-1">
              Admits {admittedCount} people •{' '}
              {formatCurrency(customerPrice / admittedCount, {
                currency: 'NGN',
                decimalPlaces: 2,
              })}{' '}
              / person
            </p>
          )}
        </div>

        <NumericFormat
          suffix={undefined}
          prefix={undefined}
          decimalScale={0}
          thousandSeparator
          customInput={TextInput}
          type="text"
          name="quantity"
          title="Quantity"
          placeholder="Quantity"
          value={values.quantity}
          onBlur={handleBlur}
          onChange={handleChange}
          errors={errors.quantity && touched.quantity ? errors.quantity : ''}
          hasIcon={false}
        />

        {eventId && (
          <p className="text-sm text-sec-black">
            Saving this tier updates the event immediately.
          </p>
        )}
        {saveError && (
          <p role="alert" className="text-danger">
            {saveError}
          </p>
        )}
        <Button
          label="Save"
          type="submit"
          className="mt-10"
          disabled={savingTier}
          loading={savingTier}
          primary
        />
      </form>
    </ModalComponent>
  )
}
